import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { ArrowBack, Visibility, VisibilityOff } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import Appbar from "../../components/Appbar";
import Footer from "../../components/Footer";
import api from "../../service/api";

const sourceOptions = [
  { value: "", label: "Todas as origens" },
  { value: "facebook-photo", label: "Facebook · Fotos" },
  { value: "facebook-video", label: "Facebook · Vídeos" },
  { value: "instagram", label: "Instagram" },
];

const visibilityOptions = [
  { value: "all", label: "Todas" },
  { value: "visible", label: "Visíveis" },
  { value: "hidden", label: "Ocultas" },
];

export default function GerenciarPublicacoes() {
  const classes = useStyles();
  const history = useHistory();
  const [source, setSource] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [offset, setOffset] = useState(0);
  const [publications, setPublications] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");
  const displayedPublications = Array.isArray(publications) ? publications : [];

  const loadPublications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        visibility,
        limit: "20",
        offset: String(offset),
      });
      if (source) params.set("source", source);
      const response = await api.get(`/publications?${params.toString()}`);
      const root = response.data;
      const payload = Array.isArray(root?.data) ? root : (root?.data ?? root);
      const items = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(root?.data)
          ? root.data
          : [];

      setPublications(items);
      setPagination(payload?.pagination || root?.pagination || null);
    } catch (requestError) {
      setError("Não foi possível carregar as publicações. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [offset, source, visibility]);

  useEffect(() => {
    loadPublications();
  }, [loadPublications]);

  const toggleVisibility = async (publication) => {
    const key = `${publication.source}-${publication.id}`;
    setUpdatingId(key);
    setError("");
    try {
      await api.patch(
        `/publications/${publication.source}/${publication.id}/visibility`,
        {
          isVisible: !publication.isVisible,
        },
      );
      await loadPublications();
    } catch (requestError) {
      setError("Não foi possível alterar a visibilidade da publicação.");
    } finally {
      setUpdatingId(null);
    }
  };

  const changeSource = (event) => {
    setSource(event.target.value);
    setOffset(0);
  };

  const changeVisibility = (event) => {
    setVisibility(event.target.value);
    setOffset(0);
  };

  return (
    <div className={classes.root}>
      <Appbar />
      <main className={classes.content}>
        <Box className={classes.container}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => history.push("/configuracoes")}
          >
            Voltar para configurações
          </Button>
          <Typography variant="h4" className={classes.title}>
            Gerenciar publicações
          </Typography>
          <Typography color="textSecondary" className={classes.description}>
            Publicações ocultas deixam de aparecer nas áreas públicas, mas
            continuam sincronizadas e podem ser exibidas novamente a qualquer
            momento.
          </Typography>

          <Box className={classes.filters}>
            <FormControl variant="outlined" className={classes.filter}>
              <InputLabel id="source-label">Origem</InputLabel>
              <Select
                labelId="source-label"
                label="Origem"
                value={source}
                onChange={changeSource}
              >
                {sourceOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" className={classes.filter}>
              <InputLabel id="visibility-label">Visibilidade</InputLabel>
              <Select
                labelId="visibility-label"
                label="Visibilidade"
                value={visibility}
                onChange={changeVisibility}
              >
                {visibilityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {error && <Typography className={classes.error}>{error}</Typography>}
          {loading ? (
            <Box className={classes.loading}>
              <CircularProgress />
              <Typography>Carregando publicações...</Typography>
            </Box>
          ) : displayedPublications.length === 0 ? (
            <Typography className={classes.empty}>
              Nenhuma publicação encontrada para os filtros selecionados.
            </Typography>
          ) : (
            <Box className={classes.list}>
              {displayedPublications.map((publication) => {
                const key = `${publication.source}-${publication.id}`;
                const updating = updatingId === key;
                return (
                  <article className={classes.card} key={key}>
                    {publication.imageUrl ? (
                      <img
                        className={classes.image}
                        src={publication.imageUrl}
                        alt="Prévia da publicação"
                      />
                    ) : (
                      <Box className={classes.imagePlaceholder}>Sem imagem</Box>
                    )}
                    <Box className={classes.cardContent}>
                      <Chip
                        size="small"
                        label={publication.sourceLabel}
                        className={classes.chip}
                      />
                      <Typography className={classes.cardTitle}>
                        {publication.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {publication.publishedAt
                          ? new Date(publication.publishedAt).toLocaleString(
                              "pt-BR",
                            )
                          : "Data não informada"}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color={publication.isVisible ? "secondary" : "primary"}
                      startIcon={
                        publication.isVisible ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )
                      }
                      disabled={updating}
                      onClick={() => toggleVisibility(publication)}
                      className={classes.action}
                    >
                      {updating
                        ? "Salvando..."
                        : publication.isVisible
                          ? "Ocultar"
                          : "Exibir"}
                    </Button>
                  </article>
                );
              })}
            </Box>
          )}
          {pagination && pagination.total > 0 && (
            <Box className={classes.pager}>
              <Typography variant="body2" color="textSecondary">
                Página {pagination.page} de {pagination.totalPages} ·{" "}
                {pagination.total} publicações
              </Typography>
              <Box className={classes.pagerActions}>
                <Button
                  variant="outlined"
                  disabled={!pagination.hasPrev || loading}
                  onClick={() =>
                    setOffset(Math.max(0, offset - pagination.limit))
                  }
                >
                  Anterior
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!pagination.hasNext || loading}
                  onClick={() => setOffset(offset + pagination.limit)}
                >
                  Próxima
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </main>
      <Footer />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: { minHeight: "100vh", display: "flex", flexDirection: "column" },
  content: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  container: { width: "100%", maxWidth: 1000 },
  title: {
    marginTop: theme.spacing(2),
    color: theme.colors?.gray1 || theme.palette.text.primary,
  },
  description: { marginTop: theme.spacing(1), maxWidth: 760 },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  filter: { minWidth: 230 },
  list: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  pager: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  pagerActions: { display: "flex", gap: theme.spacing(1) },
  card: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: theme.spacing(1),
    backgroundColor: "#fff",
    [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
  },
  image: {
    width: 96,
    height: 72,
    objectFit: "cover",
    borderRadius: theme.spacing(0.5),
    backgroundColor: "#f1f1f1",
  },
  imagePlaceholder: {
    width: 96,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: theme.spacing(0.5),
    backgroundColor: "#f1f1f1",
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  cardContent: { flex: 1, minWidth: 180 },
  chip: { marginBottom: theme.spacing(1) },
  cardTitle: {
    fontWeight: 600,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  action: { minWidth: 110 },
  loading: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  error: { marginBottom: theme.spacing(2), color: theme.palette.error.main },
}));
